// @flow
import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
// eslint-disable-next-line pinterest/no-deprecated-imports
import LegacyModal from 'app/common/react/ui/deprecated/LegacyModal';
import PropTypes from 'prop-types';
import Pulsar from 'app/common/react/ui/Pulsar';
import { Box, Button, Flyout, Text } from 'gestalt';

type Props = {
  anchor: null | HTMLElement | React.Component<any>,
  children?: any,
  overflow: 'normal' | 'breakWord',
  handleComplete: Function,
  handleDismiss: Function,
  tooltip?: {
    cancelButtonText?: string,
    confirmButtonText?: string,
    mainText: string,
    idealDirection?: 'up' | 'right' | 'down' | 'left',
  },
  isModal?: boolean,
  tooltipOnly?: boolean,
  pulserOnly?: boolean,
};

type State = {
  isPulsing: boolean,
  isModalOpen: boolean,
  positionStyles: Object,
  style: Object,
};

const modalStyle = {
  overlay: {
    padding: 0,
  },
  content: {
    boxShadow: 'none',
    height: '100%',
    margin: 0,
    outline: 'none',
    padding: 0,
    width: '100%',
  },
};

export default class LegacyToolTipPulsar extends Component<Props, State> {
  static propTypes = {
    anchor: PropTypes.node,
    children: PropTypes.node,
    overflow: PropTypes.string,
    handleComplete: PropTypes.func.isRequired,
    handleDismiss: PropTypes.func.isRequired,
    tooltip: PropTypes.shape({
      cancelButtonText: PropTypes.string,
      confirmButtonText: PropTypes.string,
      mainText: PropTypes.string,
      idealDirection: PropTypes.string,
    }),
    isModal: PropTypes.bool,
    tooltipOnly: PropTypes.bool,
    pulserOnly: PropTypes.bool,
  };

  static defaultProps = {
    overflow: 'breakWord',
    tooltipOnly: false,
    pulserOnly: false,
  };

  constructor(props: Props) {
    super(props);

    if (props.anchor) {
      // $FlowFixMe: findDOMNode returns `null | Element | Text` but Flyout needs HTMLElement
      this.anchorElement = findDOMNode(props.anchor);
    }
  }

  state: State = {
    isPulsing: !this.props.tooltipOnly,
    isModalOpen: !!this.props.isModal && !!this.props.tooltipOnly && !this.props.pulserOnly,
    positionStyles: {},
    style: {},
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.anchor !== this.props.anchor) {
      // $FlowFixMe: findDOMNode returns `null | Element | Text` but Flyout needs HTMLElement
      this.anchorElement = findDOMNode(nextProps.anchor);
    }
  }

  componentDidUpdate() {
    if (this.state.isModalOpen) {
      this.updateModalPosition();
    }
  }

  onPulsarClick = (evt: SyntheticEvent<>) => {
    if (evt) {
      // HACKY: a click on a pulser that is a child of a NavigateLink (<a>) will bubble up
      // causing an unintended redirect even with stopPropogation.
      // The preventDefault is needed for intended behavior despite this being a div.
      // As an example, see Closeup.js's Image NavigateLink + click on the WrappedImage pulser.
      // Ask @kdavison (Growth NUX) for more context.
      evt.stopPropagation();
      evt.preventDefault();
    }

    const newState = { isPulsing: false, style: {} };
    const { isModal, pulserOnly, handleComplete, anchor } = this.props;

    if (pulserOnly) {
      handleComplete();
    } else if (isModal) {
      const componentDOM = findDOMNode(anchor);

      if (componentDOM instanceof HTMLElement) {
        const boundingRectObject = componentDOM.getBoundingClientRect();
        this.setState({
          ...newState,
          isModalOpen: true,
          positionStyles: {
            top: boundingRectObject.top,
            left: boundingRectObject.left,
            position: 'absolute',
          },
        });
        return;
      }
    }

    this.setState(newState);
  };

  getPulser() {
    // $FlowFixMe: Expected to have only HTMLElement
    return <Pulsar anchor={this.props.anchor} onTouch={this.onPulsarClick} />;
  }

  getFlyout() {
    const { overflow, tooltip, handleDismiss, handleComplete } = this.props;
    if (tooltip) {
      const { cancelButtonText, confirmButtonText, mainText, idealDirection = 'down' } = tooltip;

      const displayButtons =
        cancelButtonText && handleDismiss && confirmButtonText && handleComplete;

      return (
        <Flyout
          anchor={this.anchorElement}
          color="darkGray"
          idealDirection={idealDirection}
          onDismiss={handleDismiss}
          shouldFocus={false}
          size="md"
        >
          <Box column={12} padding={3}>
            <Text bold color="white" size="lg" overflow={overflow}>
              {mainText}
            </Text>
            {displayButtons ? (
              <Box display="flex" alignItems={'center'} deprecatedMargin={{ top: 2 }}>
                <Box deprecatedMargin={{ right: 1 }} xs={{ column: 6 }}>
                  <Button
                    color="transparent"
                    onClick={handleDismiss}
                    size="md"
                    text={String(cancelButtonText)}
                  />
                </Box>
                <Box xs={{ column: 6 }}>
                  <Button
                    color="white"
                    size="md"
                    text={String(confirmButtonText)}
                    onClick={handleComplete}
                  />
                </Box>
              </Box>
            ) : null}
          </Box>
        </Flyout>
      );
    }
    return <div />;
  }

  modalPositioned = false;

  updateModalPosition() {
    const placingModal = this.state.isModalOpen && !this.modalPositioned;
    if (placingModal) {
      const componentDOM = findDOMNode(this.props.anchor);
      if (componentDOM instanceof HTMLElement) {
        const boundingRectObject = componentDOM.getBoundingClientRect();

        // Border + negative margin leaks outside of the ref element that
        // LegacyToolTipPulsar anchors on.
        // This updates the top/left calculation to account for this special case.
        // Ex: <SaveButton>
        // Ask @kdavison (Growth - NUX) for more context.
        const componentStyle = window.getComputedStyle(componentDOM);
        const border = componentStyle.getPropertyValue('border');
        const margin = componentStyle.getPropertyValue('margin');
        const borderSize = parseInt(border.split('px')[0], 10);
        const marginSize = parseInt(margin.split('px')[0], 10);
        const validNums = !isNaN(borderSize) && !isNaN(marginSize);
        const posOffset = validNums && Math.abs(marginSize) === borderSize ? borderSize : 0;

        this.setState({
          positionStyles: {
            top: boundingRectObject.top + posOffset,
            left: boundingRectObject.left + posOffset,
            position: 'absolute',
          },
        });
        this.modalPositioned = true;
      }
    }
  }

  anchorElement: ?HTMLElement;

  renderFlyout() {
    const { handleComplete, children, pulserOnly } = this.props;
    const { isModalOpen, positionStyles } = this.state;

    const showTooltip = !pulserOnly;
    if (isModalOpen) {
      return (
        <LegacyModal noButton onHide={handleComplete} style={modalStyle}>
          <div style={positionStyles}>
            {children}
            {this.getFlyout()}
          </div>
        </LegacyModal>
      );
    } else if (showTooltip) {
      return this.getFlyout();
    }
    return null;
  }

  render() {
    return this.props.anchor ? (
      <div style={this.state.style}>
        {this.state.isPulsing ? this.getPulser() : this.renderFlyout()}
      </div>
    ) : null;
  }
}
